import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Ban, UserX } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sampleImages = [
  'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=100'
];

const Blocked = () => {
  const navigate = useNavigate();
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/blocked`);
      setBlockedUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch blocked users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId) => {
    setUnblocking(userId);
    try {
      await axios.post(`${API}/users/unblock`, { user_id: userId });
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User unblocked');
    } catch (error) {
      toast.error('Failed to unblock user');
    } finally {
      setUnblocking(null);
    }
  };

  const getPhotoUrl = (user) => {
    if (user?.public_photos && user.public_photos.length > 0) {
      const photo = user.public_photos[0];
      if (photo.startsWith('data:') || photo.startsWith('http')) {
        return photo;
      }
      return `data:image/jpeg;base64,${photo}`;
    }
    return sampleImages[0];
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-[#FF2E63]">Blocked Users</h1>
        </div>
      </header>

      <main className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : blockedUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Ban className="w-12 h-12 text-[#52525B] mb-4" />
            <h3 className="text-lg font-semibold text-[#EDEDED] mb-2">No blocked users</h3>
            <p className="text-sm text-[#A1A1AA]">
              Users you block won't be able to see you or contact you
            </p>
          </div>
        ) : (
          <div className="glass rounded-xl divide-y divide-white/5">
            {blockedUsers.map((user) => (
              <div 
                key={user.id}
                className="flex items-center justify-between p-4"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={getPhotoUrl(user)}
                    alt={user.display_name || user.username}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-[#EDEDED]">
                      {user.display_name || user.username}
                    </h3>
                    <p className="text-xs text-[#52525B]">@{user.username}</p>
                  </div>
                </div>
                <Button
                  data-testid={`unblock-${user.id}`}
                  onClick={() => handleUnblock(user.id)}
                  disabled={unblocking === user.id}
                  variant="ghost"
                  className="text-[#FF2E63] hover:bg-[#FF2E63]/10"
                >
                  {unblocking === user.id ? (
                    <div className="w-4 h-4 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Unblock'
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 p-4 glass rounded-xl">
          <h4 className="text-sm font-semibold text-[#A1A1AA] mb-2">About Blocking</h4>
          <ul className="text-xs text-[#52525B] space-y-2">
            <li>• Blocked users cannot see your profile or photos</li>
            <li>• They cannot send you messages</li>
            <li>• They won't see you in the grid</li>
            <li>• Blocking is mutual - you also won't see them</li>
            <li>• Unblocking allows them to see and contact you again</li>
          </ul>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default Blocked;
